import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, Calendar, Users, Handshake, BarChart3, Shield,
  GraduationCap, BookOpen, ChevronRight, Check,
  ArrowRight, Star, ChevronLeft, Download,
  Zap, QrCode, Video, Layers, Smartphone, Languages,
  Bell, Search, Lock, FileText, Image,
  UserCheck, ClipboardList, TrendingUp, Award,
  MapPin, Phone, Building2,
} from 'lucide-react';
import './HomePage.css';

/* ────────────────────────────────────────────────────────────────────────
   Reveal-on-scroll: lightweight replacement for framer-motion's
   `motion.div` + `useInView` + variants (fadeUp / fadeLeft / fadeRight / stagger).
   No extra dependency — plain IntersectionObserver + CSS transitions.
   ──────────────────────────────────────────────────────────────────────── */
function useInView(ref, { once = true, margin = '0px' } = {}) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin: margin, threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, once, margin]);
  return inView;
}

function Reveal({ as: Tag = 'div', direction = 'up', delay = 0, className = '', style = {}, children, ...rest }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const dirClass = { up: 'pa-reveal--up', left: 'pa-reveal--left', right: 'pa-reveal--right' }[direction] || 'pa-reveal--up';
  return (
    <Tag
      ref={ref}
      className={`pa-reveal ${dirClass} ${inView ? 'pa-reveal--visible' : ''} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ─── Count-up Hook (unchanged from original, no dependency) ─────────────── */
function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    let frame;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [start, target, duration]);
  return count;
}

/* ─── Stat Card ────────────────────────────────────────────────────────── */
function StatCard({ value, suffix, label, started }) {
  const count = useCountUp(value, 2000, started);
  return (
    <div className={label ? 'pa-stat pa-stat--labeled' : 'pa-stat'}>
      <div className={label ? 'pa-stat__value pa-stat__value--large' : 'pa-stat__value'}>
        {count}{suffix}
      </div>
      {label && <div className="pa-stat__label">{label}</div>}
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────────────────── */
export default function HomePage() {
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: '-100px' });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [searchCity, setSearchCity] = useState('');
  const [searchType, setSearchType] = useState('');

  useEffect(() => {
    document.title = 'Pontis Acadexus — Connecting Educational Institutions With Global Opportunities';
    // If you're using react-helmet / react-helmet-async elsewhere in the app,
    // swap this effect for a <Helmet> block with the same meta tags as the
    // original index.tsx (description, canonical, og:*, twitter:card, JSON-LD).
  }, []);

  const testimonials = [
    {
      quote: "Pontis Acadexus transformed how we connect our students with international opportunities. We've hosted 12 university fairs this year alone, and our students now have direct access to universities from the UK, USA, and Australia.",
      name: 'Dr. Priya Sharma',
      title: 'Principal',
      institution: 'Delhi International School',
      initials: 'PS',
      color: '#8B1A2E',
    },
    {
      quote: "The platform has completely transformed our international recruitment strategy. We've doubled our international enrolments in just one academic year and built partnerships across 15 new countries.",
      name: 'Prof. James Mitchell',
      title: 'Director of International Affairs',
      institution: 'University of Melbourne',
      initials: 'JM',
      color: '#C4415A',
    },
    {
      quote: "As an international recruitment manager, Pontis Acadexus gives us unparalleled access to quality institutions. The verification system ensures every connection is legitimate and valuable.",
      name: 'Sarah Chen',
      title: 'International Recruitment Manager',
      institution: 'Oxford Global Education',
      initials: 'SC',
      color: '#B8860B',
    },
    {
      quote: "Our students now have access to over 200 international universities through a single platform. The event management tools are exceptional — everything from invitations to post-event reporting is seamless.",
      name: 'Rajesh Kumar',
      title: 'Student Counselor',
      institution: 'Greenfield International School',
      initials: 'RK',
      color: '#8B1A2E',
    },
  ];

  const whyJoinItems = [
    {
      num: '01',
      title: 'Increase Institutional Visibility',
      desc: 'Showcase your institution to thousands of schools, universities, and students across India and 50+ countries. Build a verified profile that establishes trust and credibility in the global education ecosystem.',
      slot: 'pages/home/why-join-visibility',
      bullets: ['Verified institutional profile', 'Featured in global search results', 'Showcase programs and achievements', 'Reach 500+ partner institutions'],
    },
    {
      num: '02',
      title: 'Build International Partnerships',
      desc: 'Forge lasting academic collaborations with institutions worldwide. Our platform facilitates meaningful partnerships that create long-term value for both institutions and their students.',
      slot: 'pages/home/why-join-partnerships',
      bullets: ['Formal MOU facilitation tools', 'Partnership request workflows', 'Cross-border collaboration tools', 'Verified partner directory'],
    },
    {
      num: '03',
      title: 'Connect With Qualified Students',
      desc: 'Engage directly with motivated students who are actively seeking international education opportunities. Our platform ensures every student interaction is meaningful and outcome-focused.',
      slot: 'pages/home/why-join-students',
      bullets: ['Direct student engagement tools', 'Interest tracking and analytics', 'Application management', 'Student profile verification'],
    },
  ];

  const featuredEvents = [
    {
      title: 'International University Fair — Delhi',
      date: 'July 15, 2026',
      location: 'New Delhi, India',
      seats: '1,200 seats',
      type: 'University Fair',
      universities: 34,
      slot: 'pages/home/event-fair',
      tag: 'Featured',
      tagColor: '#B8860B',
    },
    {
      title: 'Global STEM Summit — Bangalore',
      date: 'August 20, 2026',
      location: 'Bangalore, India',
      seats: '2,000 seats',
      type: 'STEM Expo',
      universities: 42,
      slot: 'pages/home/event-stem',
      tag: 'Open',
      tagColor: '#2D7A4F',
    },
    {
      title: 'International University Showcase — Mumbai',
      date: 'August 2, 2026',
      location: 'Mumbai, India',
      seats: '800 seats',
      type: 'Showcase',
      universities: 18,
      slot: 'pages/home/event-showcase',
      tag: 'Upcoming',
      tagColor: '#8B1A2E',
    },
  ];

  const globalPartners = [
    { country: 'India', count: '320+', flag: '🇮🇳', desc: 'Schools & Universities' },
    { country: 'United Kingdom', count: '48', flag: '🇬🇧', desc: 'Universities' },
    { country: 'United States', count: '36', flag: '🇺🇸', desc: 'Universities' },
    { country: 'Australia', count: '28', flag: '🇦🇺', desc: 'Universities' },
    { country: 'Canada', count: '22', flag: '🇨🇦', desc: 'Universities' },
    { country: 'Singapore', count: '14', flag: '🇸🇬', desc: 'Institutions' },
    { country: 'UAE', count: '18', flag: '🇦🇪', desc: 'Institutions' },
    { country: 'Germany', count: '12', flag: '🇩🇪', desc: 'Universities' },
  ];

  const newsArticles = [
    {
      category: 'International Education Trends',
      title: 'India Surpasses 1.3 Million Students Studying Abroad in 2026',
      excerpt: 'A new report reveals record-breaking numbers of Indian students pursuing higher education internationally, with the UK and Australia seeing the highest growth.',
      date: 'June 5, 2026',
      readTime: '4 min read',
      slot: 'pages/home/news-trends',
    },
    {
      category: 'University Partnership Strategies',
      title: 'How Top Universities Are Building School Networks in Emerging Markets',
      excerpt: 'Leading international universities share their strategies for building authentic, long-term partnerships with schools in India and Southeast Asia.',
      date: 'May 28, 2026',
      readTime: '6 min read',
      slot: 'pages/home/news-partnerships',
    },
    {
      category: 'Student Mobility Reports',
      title: 'The Rise of Hybrid Education Events: What Institutions Need to Know',
      excerpt: 'Post-pandemic, education fairs have evolved. Discover how hybrid events are creating new opportunities for student engagement and institutional outreach.',
      date: 'May 20, 2026',
      readTime: '5 min read',
      slot: 'pages/home/news-mobility',
    },
  ];

  const platformFeatures = [
    { icon: UserCheck, label: 'Institution Verification' },
    { icon: Calendar, label: 'Event Publishing' },
    { icon: ClipboardList, label: 'Registration Management' },
    { icon: Users, label: 'Participant Tracking' },
    { icon: Bell, label: 'Notifications' },
    { icon: BarChart3, label: 'Analytics' },
    { icon: FileText, label: 'Reporting' },
    { icon: Image, label: 'Media Galleries' },
    { icon: Lock, label: 'Secure Access' },
    { icon: Search, label: 'Search & Discovery' },
    { icon: Smartphone, label: 'Mobile Friendly' },
    { icon: Globe, label: 'Global Reach' },
  ];

  const futureFeatures = [
    { icon: Zap, label: 'AI Event Recommendations', desc: 'Smart matching based on institution profile' },
    { icon: TrendingUp, label: 'Lead Scoring', desc: 'Prioritize high-value institutional connections' },
    { icon: QrCode, label: 'QR Attendance Tracking', desc: 'Instant check-in at physical events' },
    { icon: Award, label: 'Digital Event Passes', desc: 'Branded digital passes for participants' },
    { icon: Video, label: 'Video Conferencing', desc: 'Built-in virtual event capabilities' },
    { icon: Layers, label: 'CRM Integrations', desc: 'Connect with Salesforce, HubSpot, and more' },
    { icon: Languages, label: 'Multi-language Support', desc: 'Platform available in 20+ languages' },
    { icon: Smartphone, label: 'Mobile Apps', desc: 'Native iOS and Android applications' },
  ];

  return (
    <>
      {/* ── SECTION 1: HERO ─────────────────────────────────────────────── */}
      <section className="pa-hero">
        <div className="pa-hero__video-wrap">
          <video
            src="/airo-assets/videos/pages/home/hero"
            autoPlay
            muted
            loop
            playsInline
            className="pa-hero__video"
          />
          <div className="pa-hero__fade-x" />
          <div className="pa-hero__fade-y" />
        </div>

        <div className="pa-hero__inner">
          <div className="pa-container">
            <div className="pa-hero__grid">

              {/* Left: text */}
              <div className="pa-hero__text">
                <Reveal className="pa-badge">
                  <Globe size={12} />
                  <span>Trusted by 500+ Institutions in 50+ Countries</span>
                </Reveal>

                <Reveal as="h1" delay={80} className="pa-hero__title">
                  Connecting Educational Institutions With{' '}
                  <span className="pa-hero__title-accent">Global Opportunities</span>
                </Reveal>

                <Reveal as="p" delay={160} className="pa-hero__subtitle">
                  Empowering schools, colleges, and international universities to collaborate through events, student engagement, and global academic partnerships.
                </Reveal>

                <Reveal delay={240} className="pa-hero__actions">
                  <Link to="/events" className="pa-btn pa-btn--primary">
                    Explore Events <ArrowRight size={16} />
                  </Link>
                  <a href="http://154.61.75.209:3000" className="pa-btn pa-btn--outline">
                    Login <Handshake size={16} />
                  </a>
                </Reveal>

                {/* Search bar */}
                <Reveal delay={320} className="pa-search">
                  <p className="pa-search__label">Find Events</p>
                  <div className="pa-search__fields">
                    <div className="pa-search__input-wrap">
                      <MapPin size={14} className="pa-search__icon" />
                      <input
                        type="text"
                        placeholder="City or State"
                        value={searchCity}
                        onChange={(e) => setSearchCity(e.target.value)}
                        className="pa-search__input"
                      />
                    </div>
                    <div className="pa-search__row">
                      <div className="pa-search__input-wrap pa-search__input-wrap--grow">
                        <Building2 size={14} className="pa-search__icon" />
                        <select
                          value={searchType}
                          onChange={(e) => setSearchType(e.target.value)}
                          className="pa-search__select"
                        >
                          <option value="">Institution Type</option>
                          <option value="school">School</option>
                          <option value="college">College / University</option>
                          <option value="international">International University</option>
                        </select>
                      </div>
                      <Link to="/events" className="pa-btn pa-btn--primary pa-search__btn">
                        <Search size={14} /> Search
                      </Link>
                    </div>
                  </div>
                </Reveal>
              </div>

              {/* Right: floating stat cards (desktop only) */}
              <div className="pa-hero__stats-col">
                <div className="pa-hero__stats-row">
                  {[
                    { value: '500+', label: 'Partner Institutions', icon: GraduationCap, color: '#8B1A2E' },
                    { value: '50+', label: 'Countries', icon: Globe, color: '#C4415A' },
                  ].map(({ value, label, icon: Icon, color }, i) => (
                    <Reveal key={label} delay={i * 100} className="pa-float-card">
                      <div className="pa-float-card__icon" style={{ background: `${color}18` }}>
                        <Icon size={18} style={{ color }} />
                      </div>
                      <div className="pa-float-card__value">{value}</div>
                      <div className="pa-float-card__label">{label}</div>
                    </Reveal>
                  ))}
                </div>

                <Reveal delay={200} className="pa-float-card pa-float-card--wide">
                  <div className="pa-float-card__header">
                    <div className="pa-float-card__icon pa-float-card__icon--maroon">
                      <Calendar size={18} />
                    </div>
                    <div>
                      <div className="pa-float-card__value">2,000+</div>
                      <div className="pa-float-card__label">Educational Events Hosted</div>
                    </div>
                  </div>
                  <div className="pa-progress">
                    <div className="pa-progress__bar" />
                  </div>
                  <div className="pa-float-card__note">80% growth year-on-year</div>
                </Reveal>

                <div className="pa-hero__stats-row pa-hero__stats-row--end">
                  {[
                    { value: '10,000+', label: 'Students Engaged', icon: Users, color: '#8B1A2E' },
                    { value: '100+', label: 'Intl. Universities', icon: BookOpen, color: '#C4415A' },
                  ].map(({ value, label, icon: Icon, color }, i) => (
                    <Reveal key={label} delay={300 + i * 100} className="pa-float-card">
                      <div className="pa-float-card__icon" style={{ background: `${color}18` }}>
                        <Icon size={18} style={{ color }} />
                      </div>
                      <div className="pa-float-card__value">{value}</div>
                      <div className="pa-float-card__label">{label}</div>
                    </Reveal>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="pa-trust-bar">
          <div className="pa-container pa-trust-bar__inner">
            <span className="pa-trust-bar__label">Trusted by:</span>
            {['IIT Delhi', 'Oxford Global', 'NUS Singapore', 'Univ. of Melbourne', 'Cambridge Network'].map((name) => (
              <span key={name} className="pa-trust-bar__name">{name}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 2: IMPACT STATISTICS ────────────────────────────────── */}
      <section className="pa-section pa-section--dark" ref={statsRef}>
        <div className="pa-container">
          <div className="pa-section-head pa-section-head--center">
            <Reveal as="p" className="pa-eyebrow pa-eyebrow--light">Our Impact</Reveal>
            <Reveal as="h2" delay={80} className="pa-heading pa-heading--light">
              A New Era of Educational Engagement
            </Reveal>
            <Reveal as="p" delay={160} className="pa-lead pa-lead--light pa-lead--center">
              Our platform empowers schools, colleges, universities, and international institutions to collaborate through events, student outreach, and academic networking.
            </Reveal>
          </div>

          <div className="pa-impact-grid">
            {[
              { value: 500, suffix: '+', label: 'Partner Institutions' },
              { value: 50, suffix: '+', label: 'Countries Represented' },
              { value: 10000, suffix: '+', label: 'Students Engaged' },
              { value: 2000, suffix: '+', label: 'Educational Events' },
              { value: 100, suffix: '+', label: 'International Universities' },
            ].map(({ value, suffix, label }, i) => (
              <Reveal key={label} delay={i * 90} className="pa-impact-card">
                <div className="pa-impact-card__value">
                  <StatCard value={value} suffix={suffix} label="" started={statsInView} />
                </div>
                <div className="pa-impact-card__label">{label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 3: ABOUT THE PLATFORM ───────────────────────────────── */}
      <section className="pa-section pa-section--muted">
        <div className="pa-container">
          <div className="pa-about-grid">
            <Reveal direction="left" className="pa-about-image">
              <div className="pa-about-image__frame">
                <img
                  src="/airo-assets/images/pages/home/about-mission"
                  alt="Diverse university students collaborating on campus"
                  loading="lazy"
                  width={800}
                  height={600}
                />
              </div>
              <div className="pa-about-image__badge">
                <div className="pa-about-image__badge-value">500+</div>
                <div className="pa-about-image__badge-label">Institutions Connected</div>
                <div className="pa-about-image__badge-sub">Across 50+ countries</div>
              </div>
            </Reveal>

            <div>
              <Reveal as="p" direction="right" className="pa-eyebrow">About the Platform</Reveal>
              <Reveal as="h2" direction="right" delay={80} className="pa-heading">
                Building the Future of International Education Engagement
              </Reveal>
              <Reveal as="p" direction="right" delay={140} className="pa-lead">
                A comprehensive ecosystem connecting educational institutions and global universities through events, recruitment initiatives, institutional partnerships, and student engagement opportunities.
              </Reveal>
              <Reveal as="p" direction="right" delay={200} className="pa-body">
                We started Pontis Acadexus because we saw a broken system — schools struggling to connect with international universities, students missing out on global opportunities, and institutions spending enormous resources on inefficient outreach. Our platform changes all of that.
              </Reveal>
              <Reveal direction="right" delay={260} className="pa-about-features">
                {[
                  { icon: Calendar, text: 'Event Management' },
                  { icon: Handshake, text: 'Partnership Tools' },
                  { icon: BarChart3, text: 'Analytics & Insights' },
                  { icon: Shield, text: 'Enterprise Security' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="pa-about-features__item">
                    <div className="pa-about-features__icon"><Icon size={16} /></div>
                    <span>{text}</span>
                  </div>
                ))}
              </Reveal>
              <Reveal direction="right" delay={320} className="pa-about-actions">
                <Link to="/about-us" className="pa-btn pa-btn--primary">
                  Learn More <ArrowRight size={16} />
                </Link>
                <a
                  href="/data/ABOUT US.pdf"
                  download="Pontis Acadexus - About Us.pdf"
                  className="pa-btn pa-btn--outline"
                >
                  <Download size={16} /> Download PDF
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: WHO WE SERVE ──────────────────────────────────────── */}
      <section className="pa-section">
        <div className="pa-container">
          <div className="pa-section-head pa-section-head--center">
            <Reveal as="p" className="pa-eyebrow">Who We Serve</Reveal>
            <Reveal as="h2" delay={80} className="pa-heading">Built for Every Educational Institution</Reveal>
            <Reveal as="p" delay={160} className="pa-lead pa-lead--center">
              Whether you're a school, college, or international university — Pontis Acadexus has the tools you need.
            </Reveal>
          </div>

          <div className="pa-card-grid pa-card-grid--3">
            {[
              {
                icon: BookOpen,
                title: 'Schools',
                desc: 'Host educational fairs and create opportunities for students to connect with global institutions.',
                benefits: ['Host international education events', 'Connect students with universities', 'Increase global exposure', 'Access verified university partners'],
                href: '/solutions/schools',
                slot: 'pages/home/who-serve-schools',
              },
              {
                icon: GraduationCap,
                title: 'Colleges & Universities',
                desc: 'Strengthen international outreach and expand partnership networks.',
                benefits: ['Organise recruitment events', 'Build international partnerships', 'Expand academic outreach', 'Track student engagement metrics'],
                href: '/solutions/colleges',
                slot: 'pages/home/who-serve-universities',
              },
              {
                icon: Globe,
                title: 'International Universities',
                desc: 'Engage directly with students and educational institutions across India.',
                benefits: ['Discover participation opportunities', 'Recruit prospective students', 'Build institutional relationships', 'Access verified school networks'],
                href: '/solutions/international',
                slot: 'pages/home/who-serve-international',
              },
            ].map((item, i) => (
              <Reveal key={item.title} delay={i * 100} className="pa-serve-card">
                <div className="pa-serve-card__image-wrap">
                  <img
                    src={`/airo-assets/images/${item.slot}`}
                    alt={item.title}
                    loading="lazy"
                    width={500}
                    height={667}
                  />
                  <div className="pa-serve-card__gradient" />
                  <div className="pa-serve-card__content">
                    <div className="pa-serve-card__icon"><item.icon size={24} /></div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                    <ul>
                      {item.benefits.map((b) => (
                        <li key={b}><Check size={14} />{b}</li>
                      ))}
                    </ul>
                    <Link to={item.href} className="pa-serve-card__link">
                      Learn More <ChevronRight size={16} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 5: FEATURED EVENTS ───────────────────────────────────── */}
      <section className="pa-section pa-section--muted">
        <div className="pa-container">
          <div className="pa-section-header">
            <div>
              <Reveal as="p" className="pa-eyebrow">Featured Events</Reveal>
              <Reveal as="h2" delay={80} className="pa-heading pa-heading--tight">Upcoming Education Events</Reveal>
            </div>
            <Reveal delay={140}>
              <Link to="/events" className="pa-inline-link">
                View All Events <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="pa-card-grid pa-card-grid--3">
            {featuredEvents.map((event, i) => (
              <Reveal key={event.title} delay={i * 100} className="pa-event-card">
                <div className="pa-event-card__image-wrap">
                  <img
                    src={`/airo-assets/images/${event.slot}`}
                    alt={event.title}
                    loading="lazy"
                    width={700}
                    height={450}
                  />
                  <div className="pa-event-card__gradient" />
                  <div className="pa-event-card__tags">
                    <span className="pa-event-card__tag" style={{ backgroundColor: event.tagColor }}>{event.tag}</span>
                    <span className="pa-event-card__type">{event.type}</span>
                  </div>
                </div>
                <div className="pa-event-card__body">
                  <h3>{event.title}</h3>
                  <div className="pa-event-card__meta">
                    <div><Calendar size={14} />{event.date}</div>
                    <div><MapPin size={14} />{event.location}</div>
                    <div><Users size={14} />{event.seats} · {event.universities} Universities</div>
                  </div>
                  <Link to="/events" className="pa-btn pa-btn--primary pa-btn--block">
                    Register Interest <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 6: WHY INSTITUTIONS JOIN ─────────────────────────────── */}
      <section className="pa-section">
        <div className="pa-container">
          <div className="pa-section-head pa-section-head--center pa-section-head--wide">
            <Reveal as="p" className="pa-eyebrow">Why Institutions Join</Reveal>
            <Reveal as="h2" delay={80} className="pa-heading">Everything You Need to Connect &amp; Grow</Reveal>
            <Reveal as="p" delay={160} className="pa-lead pa-lead--center">
              Purpose-built for educational institutions, our platform delivers the tools, insights, and connections that drive real outcomes.
            </Reveal>
          </div>

          <div className="pa-why-list">
            {whyJoinItems.map((item, i) => (
              <div key={item.num} className={`pa-why-row ${i % 2 === 1 ? 'pa-why-row--reverse' : ''}`}>
                <Reveal direction={i % 2 === 0 ? 'left' : 'right'} className="pa-why-row__image">
                  <img
                    src={`/airo-assets/images/${item.slot}`}
                    alt={item.title}
                    loading="lazy"
                    width={700}
                    height={500}
                  />
                </Reveal>
                <Reveal direction={i % 2 === 0 ? 'right' : 'left'} className="pa-why-row__content">
                  <span className="pa-why-row__number">{item.num}</span>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                  <ul>
                    {item.bullets.map((b) => (
                      <li key={b}><span className="pa-why-row__check"><Check size={12} /></span>{b}</li>
                    ))}
                  </ul>
                  <Link to="/request-demo" className="pa-inline-link pa-inline-link--bold">
                    Learn More <ArrowRight size={16} />
                  </Link>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 7: GLOBAL PARTNER NETWORK ───────────────────────────── */}
      <section className="pa-section pa-section--muted">
        <div className="pa-container">
          <div className="pa-section-head pa-section-head--center">
            <Reveal as="p" className="pa-eyebrow">Global Partner Network</Reveal>
            <Reveal as="h2" delay={80} className="pa-heading">A Truly Global Education Ecosystem</Reveal>
            <Reveal as="p" delay={160} className="pa-lead pa-lead--center">
              Institutions from across the world trust Pontis Acadexus to connect them with the right partners and students.
            </Reveal>
          </div>

          {/* World map SVG */}
          <Reveal className="pa-map-wrap">
            <div className="pa-map">
              <svg viewBox="0 0 900 400" className="pa-map__svg" fill="none">
                {[[120,80],[140,90],[160,100],[130,110],[150,120],[170,130],[140,140],[160,150],[180,140],[200,130],[190,110],[210,100],[230,90]].map(([x,y],i) => <circle key={`na${i}`} cx={x} cy={y} r="3" fill="#8B1A2E" />)}
                {[[180,200],[200,210],[190,230],[210,240],[200,260],[185,280],[195,300]].map(([x,y],i) => <circle key={`sa${i}`} cx={x} cy={y} r="3" fill="#8B1A2E" />)}
                {[[380,70],[400,75],[420,70],[440,80],[460,75],[400,90],[420,95],[440,90],[460,85],[480,80],[400,100],[420,105]].map(([x,y],i) => <circle key={`eu${i}`} cx={x} cy={y} r="3" fill="#C4415A" />)}
                {[[420,140],[440,150],[430,170],[450,180],[440,200],[430,220],[445,240],[435,260]].map(([x,y],i) => <circle key={`af${i}`} cx={x} cy={y} r="3" fill="#8B1A2E" />)}
                {[[560,80],[580,75],[600,80],[620,85],[640,80],[660,85],[580,100],[600,95],[620,100],[640,95],[660,100],[580,115],[600,110],[620,115],[640,110],[560,130],[580,125],[600,130]].map(([x,y],i) => <circle key={`as${i}`} cx={x} cy={y} r="3" fill="#8B1A2E" />)}
                {[[600,140],[615,145],[610,160],[620,170],[605,180]].map(([x,y],i) => <circle key={`in${i}`} cx={x} cy={y} r="5" fill="#8B1A2E" />)}
                {[[700,240],[720,235],[740,240],[730,260],[710,265],[720,280]].map(([x,y],i) => <circle key={`au${i}`} cx={x} cy={y} r="3" fill="#C4415A" />)}
              </svg>
              <div className="pa-map__lines">
                <svg viewBox="0 0 900 400" className="pa-map__svg">
                  {[
                    'M 610 160 Q 500 100 430 80',
                    'M 610 160 Q 650 200 720 250',
                    'M 610 160 Q 550 130 430 80',
                    'M 610 160 Q 400 120 200 120',
                  ].map((d, i) => (
                    <path key={i} d={d} fill="none" stroke="#8B1A2E" strokeWidth="1.5" strokeDasharray="6 4">
                      <animate attributeName="stroke-dashoffset" values="0;-20" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
                    </path>
                  ))}
                </svg>
              </div>
            </div>
          </Reveal>

          {/* Country grid */}
          <div className="pa-network-grid">
            {globalPartners.map((p, i) => (
              <Reveal key={p.country} delay={i * 60} className="pa-network-card">
                <div className="pa-network-card__flag">{p.flag}</div>
                <div className="pa-network-card__value">{p.count}</div>
                <div className="pa-network-card__country">{p.country}</div>
                <div className="pa-network-card__desc">{p.desc}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 8: PLATFORM FEATURES ─────────────────────────────────── */}
      <section className="pa-section pa-section--muted">
        <div className="pa-container">
          <div className="pa-section-head pa-section-head--center">
            <Reveal as="p" className="pa-eyebrow">Platform Features</Reveal>
            <Reveal as="h2" delay={80} className="pa-heading">Everything Included, Out of the Box</Reveal>
            <Reveal as="p" delay={160} className="pa-lead pa-lead--center">
              No add-ons, no hidden fees. Every feature you need to run a world-class education engagement programme.
            </Reveal>
          </div>

          <div className="pa-feature-grid">
            {platformFeatures.map(({ icon: Icon, label }, i) => (
              <Reveal key={label} delay={i * 40} className="pa-feature-chip">
                <div className="pa-feature-chip__icon"><Icon size={22} /></div>
                <span>{label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 9: TESTIMONIALS ──────────────────────────────────────── */}
      <section className="pa-section">
        <div className="pa-container">
          <div className="pa-section-head pa-section-head--center">
            <Reveal as="p" className="pa-eyebrow">Success Stories</Reveal>
            <Reveal as="h2" delay={80} className="pa-heading">Trusted by Education Leaders Worldwide</Reveal>
          </div>

          <div className="pa-testimonial">
            <div key={activeTestimonial} className="pa-testimonial__card pa-testimonial__card--anim">
              <div className="pa-testimonial__stars">
                {[...Array(5)].map((_, i) => <Star key={i} size={20} />)}
              </div>
              <blockquote>&ldquo;{testimonials[activeTestimonial].quote}&rdquo;</blockquote>
              <div className="pa-testimonial__author">
                <div
                  className="pa-testimonial__avatar"
                  style={{ background: `linear-gradient(135deg, ${testimonials[activeTestimonial].color}, #0F172A)` }}
                >
                  {testimonials[activeTestimonial].initials}
                </div>
                <div>
                  <div className="pa-testimonial__name">{testimonials[activeTestimonial].name}</div>
                  <div className="pa-testimonial__title">{testimonials[activeTestimonial].title}</div>
                  <div className="pa-testimonial__institution">{testimonials[activeTestimonial].institution}</div>
                </div>
              </div>
            </div>

            <div className="pa-testimonial__controls">
              <button
                onClick={() => setActiveTestimonial((p) => (p - 1 + testimonials.length) % testimonials.length)}
                className="pa-testimonial__nav"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="pa-testimonial__dots">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className={`pa-testimonial__dot ${i === activeTestimonial ? 'pa-testimonial__dot--active' : ''}`}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setActiveTestimonial((p) => (p + 1) % testimonials.length)}
                className="pa-testimonial__nav"
                aria-label="Next testimonial"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 10: NEWS & INSIGHTS ──────────────────────────────────── */}
      <section className="pa-section pa-section--muted">
        <div className="pa-container">
          <div className="pa-section-header">
            <div>
              <Reveal as="p" className="pa-eyebrow">News &amp; Insights</Reveal>
              <Reveal as="h2" delay={80} className="pa-heading pa-heading--tight">Latest from the Education World</Reveal>
            </div>
            <Reveal delay={140}>
              <Link to="/resources" className="pa-inline-link">
                View All Articles <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="pa-card-grid pa-card-grid--3">
            {newsArticles.map((article, i) => (
              <Reveal key={article.title} delay={i * 100} className="pa-news-card">
                <div className="pa-news-card__image">
                  <img
                    src={`/airo-assets/images/${article.slot}`}
                    alt={article.title}
                    loading="lazy"
                    width={500}
                    height={320}
                  />
                </div>
                <div className="pa-news-card__body">
                  <div className="pa-news-card__category">{article.category}</div>
                  <h3>{article.title}</h3>
                  <p>{article.excerpt}</p>
                  <div className="pa-news-card__meta">
                    <span>{article.date}</span>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 11: FUTURE READY ─────────────────────────────────────── */}
      <section className="pa-section pa-section--pink">
        <div className="pa-container">
          <div className="pa-section-head pa-section-head--center">
            <Reveal as="p" className="pa-eyebrow">Future Ready</Reveal>
            <Reveal as="h2" delay={80} className="pa-heading">Built for the Future of Global Education</Reveal>
            <Reveal as="p" delay={160} className="pa-lead pa-lead--center">
              We're constantly innovating. Here's what's coming to the Pontis Acadexus platform.
            </Reveal>
          </div>

          <div className="pa-future-grid">
            {futureFeatures.map(({ icon: Icon, label, desc }, i) => (
              <Reveal key={label} delay={i * 60} className="pa-future-card">
                <div className="pa-future-card__icon"><Icon size={22} /></div>
                <h4>{label}</h4>
                <p>{desc}</p>
                <div className="pa-future-card__badge">
                  <span className="pa-future-card__dot" />
                  Coming Soon
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 12: CTA ──────────────────────────────────────────────── */}
      <section className="pa-cta">
        <div className="pa-cta__bg-wrap">
          <img
            src="/airo-assets/images/pages/home/cta-background"
            alt="International students celebrating global education"
            loading="lazy"
            width={1920}
            height={800}
          />
          <div className="pa-cta__gradient-1" />
          <div className="pa-cta__gradient-2" />
        </div>

        <div className="pa-container pa-cta__content">
          <Reveal className="pa-badge pa-badge--cta">
            <Globe size={14} />
            <span>Join 500+ Institutions Already Connected</span>
          </Reveal>
          <Reveal as="h2" delay={80} className="pa-cta__title">
            Join the Global Education Engagement Ecosystem
          </Reveal>
          <Reveal as="p" delay={160} className="pa-cta__subtitle">
            Build stronger partnerships, create meaningful student opportunities, and connect with institutions worldwide.
          </Reveal>
          <Reveal delay={240} className="pa-cta__actions">
            <a href="http://154.61.75.209:3000" className="pa-btn pa-btn--primary pa-btn--lg">
              Login <ArrowRight size={20} />
            </a>
            <Link to="/contact-us" className="pa-btn pa-btn--ghost pa-btn--lg">
              Contact Us <Phone size={20} />
            </Link>
          </Reveal>

          <Reveal delay={320} className="pa-cta__badges">
            {[
              { icon: Shield, text: 'No credit card required' },
              { icon: Check, text: 'Free 30-day trial' },
              { icon: Users, text: 'Dedicated onboarding support' },
              { icon: Globe, text: 'Available in 50+ countries' },
            ].map(({ icon: Icon, text }) => (
              <div key={text}>
                <Icon size={14} />
                <span>{text}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
    </>
  );
}